import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router";
import {useTranslation} from "react-i18next";

import {
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {lightBlue, lightGreen} from "@mui/material/colors";

import UserPreferencesStore from "../util/UserPreferencesStore";

const DEFAULT_ACCOUNT_REGION = 'JP';
const DEFAULT_PHONE_REGION = 'KR';

const KEY_RETURN = 'Enter';

const styles = {
    homeSearchLayout: {
        justifyContent: "center",
        alignItems: "center",
        p: 2
    },
    accountLookupPanel: {
        p: 0,
        bgcolor: lightGreen[50],
        width: {md: '35rem', lg: '45rem', xl: '48rem'}
    },
    accountLookupHeader: {
        bgcolor: lightGreen[100],
        p: 1
    },
    phoneLookupPanel: {
        p: 0,
        bgcolor: lightBlue[50],
        width: {md: '35rem', lg: '45rem', xl: '48rem'}
    },
    phoneLookupHeader: {
        bgcolor: lightBlue[100],
        p: 1
    },
    regionSelector: {
        pl: 1
    },
    numberField: {
        pr: 1
    },
    lookupActions: {
        pr: 1,
        pb: 1,
        display: 'flex',
        justifyContent: 'right'
    }
};

export default function HomeSearchView() {
    const navigate = useNavigate();
    const [localUserAccount, setLocalUserAccount] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [accountNumberRegion, setAccountNumberRegion] = useState<string>(
        () => UserPreferencesStore.read().lastSearchedAccountRegion ?? DEFAULT_ACCOUNT_REGION
    );
    const [phoneRegion, setPhoneRegion] = useState<string>(
        () => UserPreferencesStore.read().lastSearchedPhoneRegion ?? DEFAULT_PHONE_REGION
    );
    const [accountNumberLookupEnabled, setAccountNumberLookupEnabled] = useState(false);
    const [phoneNumberLookupEnabled, setPhoneNumberLookupEnabled] = useState(false);
    const {t, i18n} = useTranslation();

    function handleAccountNumberLookupClicked() {
        navigate(`/lookupcustomeraccount/?region=${accountNumberRegion}&localUserAccount=${localUserAccount}`);
    }

    function handlePhoneNumberLookupClicked() {
        navigate(`/lookupcustomerphone/?region=${phoneRegion}&phone=${phoneNumber}`);
    }

    function handleAccountNumberRegionChange(event: SelectChangeEvent) {
        setAccountNumberRegion(event.target.value);
        UserPreferencesStore.write({lastSearchedAccountRegion: event.target.value});
    }

    function handlePhoneRegionChange(event: SelectChangeEvent) {
        setPhoneRegion(event.target.value);
        UserPreferencesStore.write({lastSearchedPhoneRegion: event.target.value});
    }

    useEffect(() => {
        setAccountNumberLookupEnabled(accountNumberRegion !== null && localUserAccount !== null && localUserAccount !== "");
    }, [accountNumberRegion, localUserAccount]);

    useEffect(() => {
        setPhoneNumberLookupEnabled(phoneRegion !== null && phoneNumber !== null && phoneNumber !== "");
    }, [phoneRegion, phoneNumber]);



    return (
            <Stack direction="row"  spacing={2} sx={styles.homeSearchLayout}>
                 <Paper elevation={8} sx={styles.accountLookupPanel}>
                    <Grid container spacing={2}>
                        <Grid size={12} sx={styles.accountLookupHeader}>
                            <Typography variant='h6'>{t('views.home.lookupByAccountPanel.title')}</Typography>
                        </Grid>
                        <Grid size={4} sx={styles.regionSelector}>
                            <FormControl fullWidth>
                                <InputLabel id="region-select-label">{t('domain.common.region')}</InputLabel>
                                <Select
                                    id="region-select"
                                    labelId="region-select-label"
                                    label={t('domain.common.region')}
                                    value={accountNumberRegion}
                                    onChange={handleAccountNumberRegionChange}>
                                        <MenuItem value={"JP"}>Japan</MenuItem>
                                        <MenuItem value={"KR"}>Korea</MenuItem>
                                        <MenuItem value={"TW"}>Taiwan</MenuItem>
                                        <MenuItem value={"HK"}>Hong Kong</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                         <Grid size={8} sx={styles.numberField}>
                            <TextField
                                fullWidth
                                id='localUserAccount'
                                label={t('domain.common.customerAccountNumber')}
                                value={localUserAccount}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    setLocalUserAccount(event.target.value);
                                }}
                                onKeyDown={(e) => ( e.key === KEY_RETURN && handleAccountNumberLookupClicked())}/>
                            </Grid>
                        <Grid size={12} sx={styles.lookupActions}>
                            <Button
                                id='lookupByLocalUserAccount'
                                variant='contained'
                                onClick={handleAccountNumberLookupClicked}
                                disabled={!accountNumberLookupEnabled}>{t('common.lookup')}</Button>
                        </Grid>
                     </Grid>
                 </Paper>

                <Paper elevation={8} sx={styles.phoneLookupPanel}>
                    <Grid container spacing={2}>
                        <Grid size={12} sx={styles.phoneLookupHeader}>
                            <Typography variant='h6'>{t('views.home.lookupByPhonePanel.title')}</Typography>
                        </Grid>
                        <Grid size={4} sx={styles.regionSelector}>
                            <FormControl fullWidth>
                                <InputLabel id="phone-region-select-label" disabled={true}>{t('domain.common.region')}</InputLabel>
                                <Select
                                    id="phone-region-select"
                                    labelId="phone-region-select-label"
                                    label={t('domain.common.region')}
                                    value={phoneRegion}
                                    onChange={handlePhoneRegionChange}>
                                    <MenuItem value={'JP'}>Japan (+81)</MenuItem>
                                    <MenuItem value={'KR'}>Korea (+82)</MenuItem>
                                    <MenuItem value={'TW'}>Taiwan (+886)</MenuItem>
                                    <MenuItem value={'HK'}>Hong Kong (+852)</MenuItem>
                                    <MenuItem value={'MO'} divider={true}>Macao (+853)</MenuItem>

                                    <MenuItem value={'CN'}>China (+86)</MenuItem>
                                    <MenuItem value={'DK'}>Denmark (+45)</MenuItem>
                                    <MenuItem value={'GB'}>United Kingdom (+44)</MenuItem>
                                    <MenuItem value={'US'}>United States (+1)</MenuItem>
                                    <MenuItem value={'BM'}>Bermuda (+1441)</MenuItem>
                                    <MenuItem value={'IN'}>India (+91)</MenuItem>

                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={8} sx={styles.numberField}>
                            <TextField
                                fullWidth
                                id='customerPhoneNumber'
                                value={phoneNumber}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    setPhoneNumber(event.target.value);
                                }}
                                label={t('domain.common.customerPhoneNumber')}
                                onKeyDown={(e) => ( e.key === KEY_RETURN && handlePhoneNumberLookupClicked())}/>
                        </Grid>
                        <Grid size={12} sx={styles.lookupActions}>
                            <Button id='lookupByPhoneNumber'
                                    onClick={handlePhoneNumberLookupClicked}
                                    variant='contained'
                                    disabled={!phoneNumberLookupEnabled}>{t('common.lookup')}</Button>
                        </Grid>
                    </Grid>
                </Paper>
        </Stack>
    );
}